import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, ConfirmDialog, DropdownMenu, Popover, toast } from '@wit-support/ui';
import { SenderCapacityBadge, senderCapacityLabel } from '@wit-support/ui-domain';
import { getConversation, overrideSenderCapacity, reclassifyConversation } from '../api.js';
/**
 * Humanizes the auto-pipeline gate `reason` codes emitted by
 * `gateAutoPipeline` (`@wit-support/classification`). `ok` is the eligible
 * case and never shown here. Unknown codes fall back to the raw string.
 */
const GATE_REASON_RU = {
    // `has_other` is no longer produced by the gate (the `other` topic special-case
    // was removed); kept only to humanize already-persisted `topic_classified` events.
    has_other: 'есть посторонние темы',
    none_eligible: 'нет подходящих тем',
    multiple_eligible_or_mixed: 'несколько/смешанные темы',
    low_confidence: 'низкая уверенность',
    non_customer_sender: 'письмо от организации',
};
/**
 * `senderCapacityOverride.at` formatted for the manual-override history line.
 * Plain `Date#toLocaleString`, not `ui-domain`'s timezone-aware `Time` — this
 * value is a short inline phrase inside one paragraph of text, and `Time`
 * needs a `TimezoneProvider` ancestor the rail doesn't always have wired.
 */
function formatOverrideAt(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
/**
 * Text fragment for the «Это клиент» confirm dialog, naming which pipelines this
 * override could wake up. Filtered to ENABLED pipelines only: the auto-run path
 * (`runOnePipeline` → `loadEnabledPipelineByKey`) skips a disabled one outright,
 * so a disabled pipeline can never be what fires — listing it here would have the
 * confirmation overstate the consequence it exists to warn about. Exported as a
 * pure function (rather than inlined JSX) so it's testable without opening the
 * Radix dropdown, which doesn't open under jsdom's pointer-event model.
 *
 * `undefined` = the `['eligible-pipelines', id]` query hasn't loaded yet —
 * distinct from a loaded-but-empty (or all-disabled) result, so the dialog can
 * say so honestly instead of claiming nothing matches.
 */
export function describeEligiblePipelinesForConfirm(cache) {
    if (cache === undefined)
        return 'Список подходящих по темам пайплайнов ещё не загружен.';
    const enabledNames = cache.pipelines.filter((p) => p.enabled).map((p) => p.name);
    return enabledNames.length > 0
        ? `Подходят по темам: ${enabledNames.join(', ')}.`
        : 'Подходящих включённых пайплайнов не видно.';
}
/**
 * Copy for the «Отправитель» ConfirmDialog, keyed by which action is pending.
 *
 * BOTH actions go through the SAME confirm flow, not just «Это клиент»: the
 * gate is re-evaluated on every capacity change, manual override wins over the
 * model AND over the `llm.suppress_non_customer_pipelines` flag, and `reset`
 * un-suppresses back to the model's own verdict — if that verdict is `customer`,
 * the gate can open on the recalculation and the classifier's handler fires
 * `conversation.classified` right there, same as it would for `customer`. A
 * business-tagged conversation reset back to the model's `customer` read has
 * sent mail this way before. «Это организация» stays a direct call: it only ever
 * SUPPRESSES, never opens the gate, so it has nothing to warn about.
 *
 * The two intros are deliberately NOT identical: «Это клиент» is the operator's
 * own affirmative claim about the sender; `reset` hands the decision back to the
 * model and says so — it must not read as "confirm you know this is a
 * customer", which the copy would imply if reused verbatim.
 */
const SENDER_CAPACITY_CONFIRM_COPY = {
    customer: {
        title: 'Отметить отправителя как клиента',
        intro: 'Это снимет блок «письмо от организации» — клиенту может немедленно уйти автоматическое письмо, если для темы диалога настроен подходящий пайплайн.',
        actionLabel: 'Это клиент',
        value: 'customer',
    },
    reset: {
        title: 'Вернуть решение модели',
        intro: 'Ручное решение будет снято, и дальше действует вердикт модели. Если модель считает отправителя клиентом, автопайплайн может запуститься сразу и клиенту уйдёт письмо.',
        actionLabel: 'Вернуть решение модели',
        value: null,
    },
};
/**
 * Full `ConfirmDialog` content for a «Отправитель» action, composing the
 * per-action copy above with {@link describeEligiblePipelinesForConfirm} —
 * the SAME pipeline-listing text both actions warn with, built once so the two
 * can never drift apart. Exported (like the function above) so the pairing is
 * unit-testable without opening the Radix dropdown that triggers it.
 */
export function describeSenderCapacityConfirm(action, cache) {
    const copy = SENDER_CAPACITY_CONFIRM_COPY[action];
    return {
        title: copy.title,
        description: `${copy.intro} ${describeEligiblePipelinesForConfirm(cache)}`,
        actionLabel: copy.actionLabel,
        value: copy.value,
    };
}
/**
 * Whether the SYNC half of a capacity-change mutation (`overrideSenderCapacity`)
 * is visible yet on a freshly re-fetched conversation, compared against the
 * snapshot captured in `onMutate`. Exported as a pure function (like
 * {@link describeEligiblePipelinesForConfirm} above) so both failure modes
 * below are unit-testable without opening the Radix dropdown that triggers
 * the mutation, which doesn't open under jsdom's pointer-event model.
 *
 * MUST compare `overrideValue`, not just `senderCapacity`/`gateEligible`:
 * `sender_capacity_override` is written synchronously by the same admin-api
 * route that returns 200, but the gate recompute + any pipeline start are the
 * ASYNC half, done later by the worker's `capacity-override` handler — and it
 * re-emits `conversation.classified` ONLY on the "not eligible → eligible"
 * transition (see CLAUDE.md → «Worker handlers»). Comparing only the first
 * two fields is wrong in both directions:
 *  - false positive: `senderCapacity` can change on the very first 1.5s poll
 *    tick, before the async gate recompute has run at all (or even if the
 *    worker is down and never runs it) — the toast would then promise a
 *    completed recompute that hasn't happened, and might never happen.
 *  - false negative, and the likelier first case on prod (39/39 flagged
 *    conversations there are already `business`): marking «Это организация»
 *    on a conversation that's already `business` changes neither
 *    `senderCapacity` nor `gate.eligible` — both stay put — even though the
 *    override itself DID get written. Without `overrideValue` in the
 *    comparison this reads as "nothing happened" for 20s and then times out,
 *    on an action that fully succeeded.
 */
export function senderCapacityPollHasLanded(fresh, start) {
    return (fresh.senderCapacity !== start.senderCapacity ||
        fresh.gateEligible !== start.gateEligible ||
        fresh.overrideValue !== start.overrideValue);
}
export function ClassificationBanner({ topics, gate, conversationId, classifiedAt, canReclassify, senderCapacity, senderCapacityRationale, senderCapacityOverride = null, canOverrideSenderCapacity = false, }) {
    const qc = useQueryClient();
    const [pending, setPending] = useState(false);
    const startedAtRef = useRef(null);
    const reclassifyMut = useMutation({
        mutationFn: (scope) => reclassifyConversation(conversationId, scope),
        onMutate: () => {
            startedAtRef.current = classifiedAt;
            setPending(true);
        },
        onError: () => {
            setPending(false);
            toast.error('Не удалось запустить переклассификацию');
        },
    });
    useEffect(() => {
        if (!pending)
            return;
        const started = Date.now();
        let cancelled = false;
        const timer = setInterval(() => {
            void qc
                .fetchQuery({
                queryKey: ['conversation', conversationId],
                queryFn: () => getConversation(conversationId),
                staleTime: 0,
            })
                .then((fresh) => {
                if (cancelled)
                    return;
                if (fresh.classifiedAt && fresh.classifiedAt !== startedAtRef.current) {
                    setPending(false);
                    toast.success('Классификация обновлена');
                }
                else if (Date.now() - started > 20_000) {
                    setPending(false);
                    toast.error('Таймаут переклассификации');
                }
            })
                .catch(() => {
                /* ignore poll errors — will retry on next tick */
            });
        }, 1500);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [pending, conversationId, qc]);
    // «Это клиент» и «Вернуть решение модели» can both immediately send mail —
    // the gate is re-evaluated on every capacity change and manual override wins
    // over the model AND over the suppression flag, so a reset can open the gate
    // just as directly as an explicit `customer` (see
    // `SENDER_CAPACITY_CONFIRM_COPY`'s comment). Both go through the same
    // `ConfirmDialog`. «Это организация» stays a direct call — it only ever
    // SUPPRESSES, nothing to confirm.
    const [confirmAction, setConfirmAction] = useState(null);
    const [capacityPending, setCapacityPending] = useState(false);
    const capacityStartRef = useRef({
        senderCapacity: null,
        gateEligible: null,
        overrideValue: null,
    });
    const overrideMut = useMutation({
        mutationFn: (value) => overrideSenderCapacity(conversationId, value),
        onMutate: () => {
            capacityStartRef.current = {
                senderCapacity,
                gateEligible: gate?.eligible ?? null,
                overrideValue: senderCapacityOverride?.value ?? null,
            };
            setConfirmAction(null);
            setCapacityPending(true);
        },
        onSuccess: () => {
            // The sync half (`sender_capacity` column write) is already applied the
            // moment this response lands — the inbox row's `SenderCapacityBadge`
            // (`packages/ui-domain/src/conversation-list-item.tsx`) reads the SAME
            // effective `senderCapacity`, but from a different query
            // (`['conversations']`) that nothing here otherwise touches. Invalidate
            // it right here, not only once the poll below confirms the async gate
            // recompute — the list row must not lag behind the rail just because the
            // pipeline recompute (which the list row doesn't even depend on) is
            // still in flight.
            void qc.invalidateQueries({ queryKey: ['conversations'] });
        },
        onError: () => {
            setCapacityPending(false);
            toast.error('Не удалось изменить статус отправителя');
        },
    });
    useEffect(() => {
        if (!capacityPending)
            return;
        const started = Date.now();
        let cancelled = false;
        const timer = setInterval(() => {
            void qc
                .fetchQuery({
                queryKey: ['conversation', conversationId],
                queryFn: () => getConversation(conversationId),
                staleTime: 0,
            })
                .then((fresh) => {
                if (cancelled)
                    return;
                const changed = senderCapacityPollHasLanded({
                    senderCapacity: fresh.senderCapacity,
                    gateEligible: fresh.gate?.eligible ?? null,
                    overrideValue: fresh.senderCapacityOverride?.value ?? null,
                }, capacityStartRef.current);
                if (changed) {
                    setCapacityPending(false);
                    // Sync half (override write) is confirmed here; the gate recompute +
                    // any pipeline start are the `capacity-override` worker handler's
                    // async half and may still be in flight — see
                    // `senderCapacityPollHasLanded`'s doc comment.
                    toast.success('Статус отправителя обновлён, пересчёт автопайплайнов идёт в фоне');
                    void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
                    void qc.invalidateQueries({ queryKey: ['pipeline-runs', conversationId] });
                    void qc.invalidateQueries({ queryKey: ['eligible-pipelines', conversationId] });
                }
                else if (Date.now() - started > 20_000) {
                    setCapacityPending(false);
                    // Not "nothing happened": the mutation's HTTP response already
                    // succeeded (onError above would have fired otherwise) — this is
                    // only the confirmation poll giving up.
                    toast.error('Статус отправителя сохранён, но подтверждение не пришло за 20 секунд');
                }
            })
                .catch(() => {
                /* ignore poll errors — will retry on next tick */
            });
        }, 1500);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [capacityPending, conversationId, qc]);
    // Read-only peek at the already-fetched `PipelinesPanel` query — never issues
    // its own fetch. `undefined` = not loaded yet (rather than "empty"), so the
    // confirm dialog can say so honestly instead of claiming nothing matches.
    const eligiblePipelinesCache = qc.getQueryData([
        'eligible-pipelines',
        conversationId,
    ]);
    // `ConfirmDialog` stays mounted even while closed (matches how the rest of
    // this file mounts its dialogs) — fall back to the 'customer' copy so there's
    // always a valid title/description to render; content is moot while `open`
    // is false.
    const confirmDialogContent = describeSenderCapacityConfirm(confirmAction ?? 'customer', eligiblePipelinesCache);
    const reasonLabel = gate && !gate.eligible && gate.reason ? (GATE_REASON_RU[gate.reason] ?? gate.reason) : '';
    return (_jsxs(Card, { className: "flex flex-col gap-2 px-4 py-2.5 text-sm", children: [topics.length > 0 ? (_jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [_jsx("span", { className: "text-xs text-[var(--color-text-muted)] shrink-0", children: "\u0422\u0435\u043C\u044B:" }), topics.map((t) => (_jsxs(Popover, { children: [_jsx(Popover.Trigger, { className: "cursor-pointer", children: _jsxs(Badge, { variant: t.isPrimary ? 'accent' : 'neutral', size: "sm", children: [t.name, t.confidence !== null ? ` ${Math.round(t.confidence * 100)}%` : ''] }) }), _jsxs(Popover.Content, { className: "max-w-[260px] p-2.5 text-xs leading-snug", children: [_jsx("p", { className: "font-medium text-[var(--color-text-primary)]", children: t.name }), t.rationale ? (_jsx("p", { className: "mt-1 text-[var(--color-text-secondary)]", children: t.rationale })) : (_jsx("p", { className: "mt-1 text-[var(--color-text-muted)]", children: "\u043D\u0435\u0442 \u043E\u0431\u043E\u0441\u043D\u043E\u0432\u0430\u043D\u0438\u044F" })), t.evidence.length > 0 ? (_jsx("ul", { className: "mt-1.5 space-y-1", children: t.evidence.map((q, i) => (_jsxs("li", { className: "border-l-2 border-[var(--color-border-default)] pl-2 text-[var(--color-text-muted)] italic", children: ["\u00AB", q, "\u00BB"] }, i))) })) : null] })] }, t.key)))] })) : (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0422\u0435\u043C\u044B \u043D\u0435 \u043E\u0431\u043D\u0430\u0440\u0443\u0436\u0435\u043D\u044B" })), gate !== null ? (_jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [_jsx("p", { className: [
                            'text-xs',
                            gate.eligible
                                ? 'text-[var(--color-state-success-fg)]'
                                : 'text-[var(--color-text-muted)]',
                        ].join(' '), children: gate.eligible
                            ? 'Автопайплайн: подходит ✓'
                            : `Автопайплайн: не подходит${reasonLabel ? ` — ${reasonLabel}` : ''}` }), _jsx(SenderCapacityBadge, { capacity: senderCapacity ?? null, rationale: senderCapacityRationale ?? null })] })) : null, senderCapacityOverride ? (_jsxs("p", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0418\u0437\u043C\u0435\u043D\u0435\u043D\u043E \u0432\u0440\u0443\u0447\u043D\u0443\u044E: ", senderCapacityLabel(senderCapacityOverride.value), senderCapacityOverride.actorLabel ? `, ${senderCapacityOverride.actorLabel}` : '', `, ${formatOverrideAt(senderCapacityOverride.at)}`] })) : null, _jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [canReclassify ? (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenu.Trigger, { asChild: true, children: _jsx(Button, { variant: "ghost", size: "sm", disabled: pending, children: pending ? 'Классификация…' : 'Переклассифицировать' }) }), _jsxs(DropdownMenu.Content, { children: [_jsx(DropdownMenu.Item, { onSelect: () => reclassifyMut.mutate('first_message'), children: "\u041F\u043E \u043F\u0435\u0440\u0432\u043E\u043C\u0443 \u0441\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u044E" }), _jsx(DropdownMenu.Item, { onSelect: () => reclassifyMut.mutate('full_thread'), children: "\u041F\u043E \u0432\u0441\u0435\u043C\u0443 \u0434\u0438\u0430\u043B\u043E\u0433\u0443" })] })] })) : null, canOverrideSenderCapacity ? (_jsxs(DropdownMenu, { children: [_jsx(DropdownMenu.Trigger, { asChild: true, children: _jsx(Button, { variant: "ghost", size: "sm", disabled: capacityPending, children: capacityPending ? 'Отправитель…' : 'Отправитель' }) }), _jsxs(DropdownMenu.Content, { children: [_jsx(DropdownMenu.Item, { onSelect: () => setConfirmAction('customer'), children: "\u042D\u0442\u043E \u043A\u043B\u0438\u0435\u043D\u0442" }), _jsx(DropdownMenu.Item, { onSelect: () => overrideMut.mutate('business'), children: "\u042D\u0442\u043E \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F" }), senderCapacityOverride ? (_jsx(DropdownMenu.Item, { onSelect: () => setConfirmAction('reset'), children: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C \u0440\u0435\u0448\u0435\u043D\u0438\u0435 \u043C\u043E\u0434\u0435\u043B\u0438" })) : null] })] })) : null] }), _jsx(ConfirmDialog, { open: confirmAction !== null, onOpenChange: (open) => setConfirmAction(open ? confirmAction : null), title: confirmDialogContent.title, description: confirmDialogContent.description, cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: confirmDialogContent.actionLabel, onConfirm: () => overrideMut.mutate(confirmDialogContent.value) })] }));
}
//# sourceMappingURL=classification-banner.js.map